import { publish } from '~/event-bus';
import { getCartSectionNames } from '~/helpers';
import { HTMLCustomElement } from '~/html-custom-element';

/**
 * @typedef {{
 *   product?: {
 *     first_available_variant: {
 *       id: number;
 *     };
 *   };
 *   threshold?: number;
 *   threshold_type: 'value' | 'quantity';
 *   line_item_property_key?: string;
 *   line_item_property_value?: string;
 *   purchase_type: 'both' | 'one_time' | 'subscription';
 * }} Item
 */

class AutoAddComponent extends HTMLCustomElement {
  /**
   * @returns {{ items: Item[] }}
   */
  get config() {
    return Object.freeze(JSON.parse(this.targets.config.innerHTML));
  }

  connectedCallback() {
    if (super.connectedCallback) super.connectedCallback();

    this.subscribe('cart:update', () => {
      fetch(`${theme.routes.cart_url}.json`)
        .then((response) => response.json())
        .then((response) => {
          for (const item of this.config.items) {
            this.handleCartUpdate(response, item);
          }
        });
    });
  }

  /**
   * @param {object} cart
   * @param {Item} item
   */
  handleCartUpdate(cart, item) {
    let items = cart.items.filter((cartItem) => !(cartItem.variant_id === item.product.first_available_variant.id));
    const hasVariant = cart.items.find(({ variant_id }) => variant_id === item.product.first_available_variant.id);

    if (item.purchase_type === 'one_time') {
      items = items.filter((cartItem) => !cartItem.selling_plan_allocation);
    } else if (item.purchase_type === 'subscription') {
      items = items.filter((cartItem) => cartItem.selling_plan_allocation);
    }

    if (item.line_item_property_key && item.line_item_property_value) {
      items = items.filter(
        (cartItem) =>
          cartItem.properties &&
          cartItem.properties.hasOwnProperty(item.line_item_property_key) &&
          cartItem.properties[item.line_item_property_key] === item.line_item_property_value,
      );
    }

    const allowed =
      items.reduce(
        (accumulator, { final_line_price, quantity }) =>
          accumulator + (item.threshold_type === 'quantity' ? quantity : final_line_price),
        0,
      ) >= (item.threshold_type === 'quantity' ? item.threshold : item.threshold * 100);

    if (allowed && !hasVariant) {
      fetch(`${theme.routes.cart_add_url}.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          items: [
            {
              id: item.product.first_available_variant.id,
              quantity: 1,
            },
          ],
          sections: getCartSectionNames(),
        }),
      })
        .then((response) => response.json())
        .then((response) => {
          publish('cart:update', { sections: response.sections });
          publish('cart:added');
        });
    }
  }
}

if (!customElements.get('auto-add-component')) customElements.define('auto-add-component', AutoAddComponent);
